import 'src/styles/sieve.css';

import {
  faArrowRight,
  faBoxOpen,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ActionContext from 'src/components/action-context';
import MaterialBatchSuccessCardDetails from 'src/components/material-batch-success-card-details';
import Modal from 'src/components/modal';
import NotFound from 'src/components/not-found';
import RangeInputConversionTooltip from 'src/components/RangeInputConversionTooltip';
import Alert from 'src/components/ui/Alert';
import Button from 'src/components/ui/Button';
import Input from 'src/components/ui/Input';
import ResourceCard from 'src/components/ui/ResourceCard';
import SectionLabel from 'src/components/ui/SectionLabel';
import FeaturesContext from 'src/context/FeaturesContext';
import useSelectedAmountHandler from 'src/hooks/useSelectedAmountHandler';
import useActionLoadingStore from 'src/stores/useActionLoadingStore';
import { api } from 'src/utils/api';
import {
  API_RESOURCES,
  MATERIAL_BATCH_ACTIONS, PERMANENT_CONTAINER_ACTIONS,
} from 'src/utils/constants';
import { convertToUserUnits, formatThreeDecimalsNumber, formatUnits } from 'src/utils/conversions';
import { FEATURES, isFeatureEnabled } from 'src/utils/features';
import routes from 'src/utils/routes';
import { formatConvertedUnits } from 'src/utils/ui';
import { getRouteURI, getShortUuid, getUuid } from 'src/utils/url';
import userPropType from 'src/utils/user-prop-type';
import { formatDynamicPercentage, getInputStep } from 'src/utils/validation';

import ActionPage from './_action-wrapper';

const Sieve = ({ user }) => {
  const { uuid: batchUUID } = useParams();
  const [batch, setBatch] = useState(undefined);
  const [batchPermanentContainerData, setBatchPermanentContainerData] = useState({
    container: null,
    location: null,
    subLocation: null,
  });
  const { isLoading, setLoading } = useActionLoadingStore();
  const [error, setError] = useState(undefined);

  const [isSubmitting, setSubmitting] = useState(false);
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const navigate = useNavigate();

  const { features } = useContext(FeaturesContext);
  const isEnforceContainersEnabled = isFeatureEnabled(features, FEATURES.ENFORCE_CONTAINERS);

  const { quantity: convertedBatchQuantity, units: convertedBatchUnits, isConverted } =
    useMemo(() => {
      if (batch) {
        return convertToUserUnits(batch.quantity, batch.units);
      }
      return { quantity: '0.00', units: '', isConverted: false };
    }, [batch]);


  const {
    selectedAmountBase: remainingQuantity,
    selectedAmountConverted,
    handleSelectedAmountChange,
    handleRangeChange,
  } = useSelectedAmountHandler(
    batch?.quantity || 0,
    batch?.units || '',
    convertedBatchQuantity,
    convertedBatchUnits
  );

  useEffect(() => {
    if (batch?.quantity !== undefined) {
      handleRangeChange(batch.quantity);
    }
  }, [batch?.quantity, handleRangeChange]);

  const isEmptySieveValue = remainingQuantity === undefined || remainingQuantity === '';
  const isIncorrectSieveValue = isNaN(remainingQuantity)
    || remainingQuantity > batch?.quantity;

  const {
    containerActionData,
  } = useContext(ActionContext);

  const resetSieveBatchIntoPermanentContainerState = () => {
    // Reset the context state to its initial state
    containerActionData.resetActionContainerState();
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const batch = await api.get(`${API_RESOURCES.MATERIAL_BATCH}/${batchUUID}/`).json();
      if (batch && batch.containers.length === 1) {
        /* We have only one container attached to the batch,
         and we need to fetch the container to check if it is disposable or not. */

        const permanentContainer = await api.get(`${API_RESOURCES.MATERIAL_CONTAINER}/${getUuid(batch.containers[0])}/`).json();

        /* Only if the Container is not disposable and has the same batch - we can consider it suitable to
           sieve the batch amount to this container */

        if (!permanentContainer?.disposable) {
          const [permanentContainerLocation, permanentContainerSubLocation] = await Promise.all([
            api.get(`${API_RESOURCES.LOCATION}/${getUuid(permanentContainer.location)}/`).then(response => response.json()),
            api.get(`${API_RESOURCES.SUB_LOCATION}/${getUuid(permanentContainer.sub_location)}/`).then(response => response.json()),
          ]);

          setBatchPermanentContainerData({
            container: permanentContainer,
            location: permanentContainerLocation,
            subLocation: permanentContainerSubLocation,
          });
        }

      }
      setBatch(batch);
    } catch (error) {
      setError(error.name);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => void fetchInitialData(), []);

  if (!isLoading && !batch) {
    return <NotFound id={batchUUID} />;
  }

  const onActionSieve = async (batchUri, remainingQuantity) => {
    const payload = {
      /* eslint-disable camelcase */
      metadata: {
        remaining_quantity: remainingQuantity,
      },
      action_type: MATERIAL_BATCH_ACTIONS.SIEVE,
      source_batch: batchUri,
    };

    setSubmitting(true);
    await api.post(`${API_RESOURCES.MATERIAL_BATCH_ACTION}/`, {
      json: payload,
    });
  };

  const sieveIntoSameContainer = async (batchUri, remainingQuantity) => {
    try {
      await onActionSieve(batchUri, remainingQuantity);

      const containerizeActionPayload = [{
        uri: batchPermanentContainerData.container.uri,
        quantity: remainingQuantity,
      }];

      const containerizePayload = {
        action_type: MATERIAL_BATCH_ACTIONS.CONTAINERIZE_BATCH,
        source_batch: batchUri,
        metadata: {
          containers: containerizeActionPayload,
        },
      };

      await api.post('material-batch-action/', {
        json: containerizePayload,
      });

    } catch (error_) {
      const { errors } = await error_.response.json();
      setSubmitError(errors[0].title);
      setSubmitting(false);
      return;
    }

    navigate(getRouteURI(routes.materialBatchSuccess,
      { uuid: getUuid(batchUri) },
      { action: MATERIAL_BATCH_ACTIONS.SIEVE, permanentContainerUri: batchPermanentContainerData.container.uri }));

    return new Promise(() => {
    });
  };

  const sieveWithoutContainer = async (batchUri, remainingQuantity) => {
    try {
      await onActionSieve(batchUri, remainingQuantity);
    } catch (error_) {
      const { errors } = await error_.response.json();
      setSubmitError(errors[0].title);
      setSubmitting(false);
      return;
    }

    navigate(getRouteURI(routes.materialBatchSuccess,
      { uuid: getUuid(batchUri) },
      { action: MATERIAL_BATCH_ACTIONS.SIEVE }));

    return new Promise(() => {
    });
  };

  const lostQuantity = batch?.quantity - remainingQuantity;
  const remainingPercentage = formatDynamicPercentage(remainingQuantity, batch?.quantity);
  const lostQuantityConverted = convertedBatchQuantity - selectedAmountConverted;
  const rangePercentage = Number(remainingPercentage) || 0;
  const actionsDisabled = isSubmitting || isIncorrectSieveValue || isEmptySieveValue;
  const anotherContainerRoute = getRouteURI(routes.scan,
    {},
    {
      'material-batch': batch?.uri,
      entity: API_RESOURCES.MATERIAL_CONTAINER,
      action: PERMANENT_CONTAINER_ACTIONS.SIEVE_BATCH,
      sieveQuantity: remainingQuantity,
      initialSieveAction: true,
      batchAction: true,
    });

  const goToAnotherContainer = () => {
    resetSieveBatchIntoPermanentContainerState();
    navigate(anotherContainerRoute);
  };

  const confirmSieveWithoutContainer = () => {
    setConfirmationOpen(false);
    return sieveWithoutContainer(batch.uri, remainingQuantity);
  };

  return (
    <ActionPage
      id={batchUUID}
      user={user}
      httpError={error}
      customErrorText={submitError}
      action={MATERIAL_BATCH_ACTIONS.SIEVE}
      isLoading={isLoading}
    >
      <div className='sieve-page'>
        <Alert
          variant='info'
          icon={faBoxOpen}
          title={`Sieving batch ${getShortUuid(batch?.uri)}`}
          className='sieve-page__intro'
        >
          Record the amount of powder transferred to the destination container after sieving.
        </Alert>

        <ResourceCard
          title='Transfer quantity'
          subtitle='Set the amount of powder moved after sieving'
          className='sieve-quantity-card'
        >
          <div className='sieve-quantity-card__body'>
            <div className='sieve-quantity-card__summary'>
              <span className='text-muted'>Total Quantity in Batch</span>
              <span>
                <strong>
                  {formatThreeDecimalsNumber(batch?.quantity)} {formatUnits(batch?.units)}
                </strong>
                {isConverted && (
                  <small>
                    {formatConvertedUnits(
                      formatThreeDecimalsNumber(convertedBatchQuantity),
                      convertedBatchUnits
                    )}
                  </small>
                )}
              </span>
            </div>

            <div className='selectedAmountRow'>
              <label className='new-quantity-label text-muted' htmlFor='remainingQuantity'>
                Powder transferred
              </label>
              <div className='selectedAmountControls'>
                <Input
                  id='remainingQuantity'
                  name='remainingQuantity'
                  min='0'
                  className='selectedAmountInput'
                  step={getInputStep(convertedBatchQuantity)}
                  max={convertedBatchQuantity}
                  type='number'
                  value={selectedAmountConverted}
                  aria-invalid={isIncorrectSieveValue}
                  onChange={handleSelectedAmountChange}
                />
                <span
                  className={`selectedAmountUnits ${isIncorrectSieveValue ? 'text-danger' : 'text-primary'}`}
                >
                  {formatUnits(convertedBatchUnits)} ({remainingPercentage || 0}%)
                </span>
                {isIncorrectSieveValue && (
                  <OverlayTrigger
                    placement='top'
                    overlay={(
                      <Tooltip id='sieve-value-exceeded'>
                        Sieve quantity exceeds the batch quantity
                      </Tooltip>
                    )}
                  >
                    <FontAwesomeIcon icon={faTriangleExclamation} color='#e0910f' />
                  </OverlayTrigger>
                )}
              </div>
            </div>

            <div className='position-relative mt15'>
              <RangeInputConversionTooltip
                className='rangeInputAbsolute'
                visible={isConverted}
                defaultUnits={batch?.units || ''}
                convertedUnits={convertedBatchUnits}
              />
              <input
                name='remainingQuantityRange'
                className='selectedAmountRange'
                min='0'
                step={getInputStep(batch?.quantity, true)}
                max={batch?.quantity}
                type='range'
                value={remainingQuantity}
                style={{
                  background: `linear-gradient(to right, #007bff 0%, #007bff ${rangePercentage}%, #e5e7eb ${rangePercentage}%, #e5e7eb 100%)`,
                }}
                onChange={(e) => handleRangeChange(e.target.value)}
              />
              <div className='d-flex justify-content-between text-muted fs-mobile-12'>
                <div className='d-flex flex-column align-items-start text-left'>
                  <div>0 {formatUnits(batch?.units)}</div>
                  {isConverted && formatConvertedUnits(0, convertedBatchUnits)}
                </div>
                <div className='d-flex flex-column align-items-end text-right'>
                  <div>{formatThreeDecimalsNumber(batch?.quantity)} {formatUnits(batch?.units)}</div>
                  {isConverted && formatConvertedUnits(convertedBatchQuantity, convertedBatchUnits)}
                </div>
              </div>
            </div>
          </div>
        </ResourceCard>

        {
          batchPermanentContainerData.container && (
            <div className='sieve-page__current-container'>
              <SectionLabel>Current container</SectionLabel>
              <MaterialBatchSuccessCardDetails
                permanentContainer={batchPermanentContainerData.container}
                batch={batch}
                customQuantity={batchPermanentContainerData.quantity}
                customLocation={batchPermanentContainerData.location?.name}
                subLocation={batchPermanentContainerData.subLocation?.name}
                customMainStyle={{ marginTop: '0' }}
              />
              <Button
                fill
                block
                type='submit'
                variant='primary'
                size='large'
                loading={isSubmitting}
                disabled={actionsDisabled}
                onClick={() => sieveIntoSameContainer(batch.uri, remainingQuantity)}
              >
                Sieve into current container
              </Button>
            </div>
          )
        }

        <SectionLabel>Choose a destination</SectionLabel>
        <div className='sieve-page__actions'>
          <Button
            fill
            block
            variant='primary'
            size='large'
            icon={<FontAwesomeIcon icon={faArrowRight} />}
            iconPosition='right'
            disabled={actionsDisabled}
            onClick={goToAnotherContainer}
          >
            Sieve into another container
          </Button>

          {!isEnforceContainersEnabled && (
            <Button
              block
              type='submit'
              variant='ghost'
              size='large'
              loading={isSubmitting}
              disabled={actionsDisabled}
              onClick={() => setConfirmationOpen(true)}
            >
              Sieve without container
            </Button>
          )}

          <Link to={`/material-container?batch=${batchUUID}`} className='sieve-page__cancel-link'>
            <Button variant='link' disabled={isSubmitting}>
              Cancel
            </Button>
          </Link>
        </div>

        <Modal
          isOpen={isConfirmationOpen}
          title='Confirm sieve without container'
          dismissText='Go Back'
          confirmText='Continue'
          confirmClass='button--primary button--fill'
          dataCy='sieve-confirmation-modal'
          onConfirm={confirmSieveWithoutContainer}
          onDismiss={() => setConfirmationOpen(false)}
        >
          <p className='text-muted'>
            Review the material outcome before sieving without a destination container.
          </p>
          <div className='alert alert-success text-left'>
            <strong>Usable and marked as sieved</strong>
            <div className='sieve-confirmation-quantity d-flex align-items-center'>
              <span className='spacer-right'>
                {formatThreeDecimalsNumber(remainingQuantity)} {formatUnits(batch?.units)}
              </span>
              {isConverted && formatConvertedUnits(
                formatThreeDecimalsNumber(selectedAmountConverted),
                convertedBatchUnits
              )}
            </div>
          </div>
          <div className='alert alert-danger text-left mb0'>
            <strong>Lost during sieving</strong>
            <div className='sieve-confirmation-quantity d-flex align-items-center'>
              <span className='spacer-right'>
                {formatThreeDecimalsNumber(lostQuantity)} {formatUnits(batch?.units)}
              </span>
              {isConverted && formatConvertedUnits(
                formatThreeDecimalsNumber(lostQuantityConverted),
                convertedBatchUnits
              )}
            </div>
            <small className='d-block mt-1'>
              This material will no longer be available.
            </small>
          </div>
        </Modal>
      </div>
    </ActionPage>
  );
};

Sieve.propTypes = {
  user: userPropType,
};

Sieve.defaultProps = {
  user: null,
};

export default Sieve;
